import { DataBoxCard } from "@/components/accounting/data-box-card";
import {
  TOKEN_FIELDS,
  type TokenField,
  TokenTermsFields,
  readTokenTerms,
} from "@/components/billing/token-terms-fields";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { ApiError, api } from "@/lib/api";
import { useApiMutation } from "@/lib/use-api-mutation";
import { createFileRoute, redirect, useRouter } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";

/**
 * `/accounting/settings` — our own company as it appears on the
 * documents we issue, plus the state of every number series.
 *
 * The identity can be pulled straight from the Fakturoid account, so
 * the migration doesn't start with retyping an address.
 */

const INPUT_CLS =
  "flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2";

type Settings = {
  companyName: string;
  registrationNo: string | null;
  vatNo: string | null;
  street: string | null;
  city: string | null;
  zip: string | null;
  country: string;
  vatPayer: boolean;
  standardVatRate: string;
  bankAccount: string | null;
  iban: string | null;
  swiftBic: string | null;
  defaultDueDays: number;
  invoiceNote: string | null;
  invoiceEmail: string | null;
} & Record<TokenField, string>;

type SettingsPatch = Omit<Settings, "standardVatRate" | TokenField> & { standardVatRate: number };

type Series = {
  id: string;
  documentType: string;
  year: number;
  format: string;
  lastNumber: number;
};

type TaxFiler = {
  preparerFirstName: string;
  preparerLastName: string;
  preparerPhone: string;
  signatoryFirstName: string;
  signatoryLastName: string;
  signatoryPosition: string;
  nace: string;
};

type Response = { settings: Settings | null; series: Series[]; taxFiler: TaxFiler | null };

export const Route = createFileRoute("/_app/accounting/settings")({
  loader: async () => {
    try {
      const base = await api.get<Omit<Response, "taxFiler">>("/api/accounting/settings");
      // Without the company there is no profile to read; the form stays hidden.
      const taxFiler = base.settings
        ? await api.get<TaxFiler>("/api/accounting/settings/tax-filer").catch(() => null)
        : null;
      return { ...base, taxFiler };
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) throw redirect({ to: "/my" });
      throw err;
    }
  },
  component: SettingsPage,
});

function SettingsPage() {
  const { t } = useTranslation();
  const { settings, series, taxFiler } = Route.useLoaderData();
  const router = useRouter();

  // The shape the API accepts — not the shape settings arrive in: the
  // rate and the due days go to the server as numbers, not as input text.
  const save = useApiMutation(
    (body: SettingsPatch) => api.patch("/api/accounting/settings", body),
    {
      onSuccess: () => router.invalidate(),
      errorMessage: () => t("accSettings.saveFailed"),
    },
  );

  const importFromFakturoid = useApiMutation(
    () => api.post("/api/accounting/settings/import-from-fakturoid", {}),
    {
      onSuccess: () => router.invalidate(),
      errorMessage: () => t("accSettings.importFailed"),
    },
  );

  async function submit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const fd = new FormData(e.currentTarget);
    const text = (key: string) => ((fd.get(key) as string) ?? "").trim() || null;

    await save.mutate({
      companyName: (fd.get("companyName") as string).trim(),
      registrationNo: text("registrationNo"),
      vatNo: text("vatNo"),
      street: text("street"),
      city: text("city"),
      zip: text("zip"),
      country: (fd.get("country") as string).trim() || "CZ",
      vatPayer: fd.get("vatPayer") === "on",
      standardVatRate: Number(fd.get("standardVatRate")),
      bankAccount: text("bankAccount"),
      iban: text("iban"),
      swiftBic: text("swiftBic"),
      defaultDueDays: Number(fd.get("defaultDueDays")),
      invoiceNote: text("invoiceNote"),
      invoiceEmail: text("invoiceEmail"),
    });
  }

  return (
    <div className="space-y-6 p-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="font-semibold text-2xl">{t("accSettings.title")}</h1>
          <p className="text-muted-foreground text-sm">{t("accSettings.subtitle")}</p>
        </div>
        <Button
          variant="outline"
          disabled={importFromFakturoid.pending}
          onClick={() => importFromFakturoid.mutate()}
        >
          {importFromFakturoid.pending
            ? t("accSettings.reading")
            : t("accSettings.importFromFakturoid")}
        </Button>
      </div>

      {(save.error || importFromFakturoid.error) && (
        <p className="text-destructive text-sm">{save.error ?? importFromFakturoid.error}</p>
      )}

      <Card>
        <CardHeader>
          <CardTitle>{t("accSettings.company")}</CardTitle>
          <CardDescription>
            {settings ? t("accSettings.companyHint") : t("accSettings.companyEmptyHint")}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={submit} className="space-y-4">
            <Field
              name="companyName"
              label={t("accSettings.companyName")}
              required
              defaultValue={settings?.companyName ?? ""}
            />
            <div className="grid gap-4 sm:grid-cols-2">
              <Field
                name="registrationNo"
                label={t("accSettings.registrationNo")}
                defaultValue={settings?.registrationNo}
              />
              <Field name="vatNo" label={t("accSettings.vatNo")} defaultValue={settings?.vatNo} />
            </div>
            <Field name="street" label={t("accSettings.street")} defaultValue={settings?.street} />
            <div className="grid gap-4 sm:grid-cols-3">
              <Field name="zip" label={t("accSettings.zip")} defaultValue={settings?.zip} />
              <Field name="city" label={t("accSettings.city")} defaultValue={settings?.city} />
              <Field
                name="country"
                label={t("accSettings.country")}
                defaultValue={settings?.country ?? "CZ"}
              />
            </div>

            <div className="grid gap-4 sm:grid-cols-3">
              <Field
                name="bankAccount"
                label={t("accSettings.bankAccount")}
                defaultValue={settings?.bankAccount}
              />
              <Field name="iban" label={t("accSettings.iban")} defaultValue={settings?.iban} />
              <Field
                name="swiftBic"
                label={t("accSettings.swiftBic")}
                defaultValue={settings?.swiftBic}
              />
            </div>

            <div className="grid items-end gap-4 sm:grid-cols-3">
              <label className="flex items-center gap-2 text-sm" htmlFor="vatPayer">
                <input
                  id="vatPayer"
                  name="vatPayer"
                  type="checkbox"
                  defaultChecked={settings?.vatPayer ?? true}
                  className="h-4 w-4"
                />
                {t("accSettings.vatPayer")}
              </label>
              <Field
                name="standardVatRate"
                label={t("accSettings.standardVatRate")}
                type="number"
                defaultValue={settings ? String(Number(settings.standardVatRate)) : "21"}
              />
              <Field
                name="defaultDueDays"
                label={t("accSettings.dueDays")}
                type="number"
                defaultValue={String(settings?.defaultDueDays ?? 14)}
              />
            </div>

            <Field
              name="invoiceEmail"
              label={t("accSettings.invoiceEmail")}
              type="email"
              defaultValue={settings?.invoiceEmail}
            />
            <Field
              name="invoiceNote"
              label={t("accSettings.invoiceNote")}
              defaultValue={settings?.invoiceNote}
            />

            <Button type="submit" disabled={save.pending}>
              {save.pending ? t("accSettings.saving") : t("accSettings.save")}
            </Button>
          </form>
        </CardContent>
      </Card>

      {settings && <TokenTermsCard settings={settings} />}

      {taxFiler && <TaxFilerCard taxFiler={taxFiler} />}

      <DataBoxCard />

      <Card>
        <CardHeader>
          <CardTitle>{t("accSettings.numberSeries")}</CardTitle>
          <CardDescription>{t("accSettings.numberSeriesHint")}</CardDescription>
        </CardHeader>
        <CardContent>
          {series.length === 0 ? (
            <p className="text-muted-foreground text-sm">{t("accSettings.noSeries")}</p>
          ) : (
            <table className="w-full text-sm">
              <thead className="text-left text-muted-foreground">
                <tr>
                  <th className="py-1 pr-4 font-medium">{t("accSettings.type")}</th>
                  <th className="py-1 pr-4 font-medium">{t("accSettings.year")}</th>
                  <th className="py-1 pr-4 font-medium">{t("accSettings.format")}</th>
                  <th className="py-1 font-medium">{t("accSettings.lastNumber")}</th>
                </tr>
              </thead>
              <tbody>
                {series.map((row) => (
                  <tr key={row.id} className="border-t">
                    <td className="py-1 pr-4">
                      <Badge variant="outline">{row.documentType}</Badge>
                    </td>
                    <td className="py-1 pr-4">{row.year}</td>
                    <td className="py-1 pr-4 font-mono text-xs">{row.format}</td>
                    <td className="py-1 tabular-nums">{row.lastNumber}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function Field({
  name,
  label,
  defaultValue,
  required,
  type = "text",
}: {
  name: string;
  label: string;
  defaultValue?: string | null | undefined;
  required?: boolean | undefined;
  type?: string | undefined;
}) {
  return (
    <div className="space-y-2">
      <label htmlFor={name} className="font-medium text-sm leading-none">
        {label}
        {required && <span className="text-destructive"> *</span>}
      </label>
      <input
        id={name}
        name={name}
        type={type}
        required={required}
        defaultValue={defaultValue ?? ""}
        className={INPUT_CLS}
      />
    </div>
  );
}

/** Company defaults of the Netlion token terms; a customer can override each one. */
function TokenTermsCard({ settings }: { settings: Settings }) {
  const { t } = useTranslation();
  const router = useRouter();
  const save = useApiMutation(
    (body: Record<TokenField, number>) => api.patch("/api/accounting/settings", body),
    {
      onSuccess: () => router.invalidate(),
      errorMessage: () => t("accSettings.saveFailed"),
    },
  );

  async function submit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const read = readTokenTerms(new FormData(e.currentTarget));
    // The company value can't be left empty, an empty field keeps what's saved.
    const body = Object.fromEntries(
      TOKEN_FIELDS.map((f) => [f, read[f] ?? Number(settings[f])]),
    ) as Record<TokenField, number>;
    await save.mutate(body);
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t("netlionTokens.settingsTitle")}</CardTitle>
        <CardDescription>{t("netlionTokens.settingsHint")}</CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={submit} className="space-y-4" data-testid="token-terms-form">
          <TokenTermsFields values={settings} />
          {save.error && <p className="text-destructive text-sm">{save.error}</p>}
          <Button type="submit" disabled={save.pending}>
            {save.pending ? t("accSettings.saving") : t("accSettings.save")}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}

const TAX_FILER_FIELDS = [
  "preparerFirstName",
  "preparerLastName",
  "preparerPhone",
  "signatoryFirstName",
  "signatoryLastName",
  "signatoryPosition",
  "nace",
] as const;

function TaxFilerCard({ taxFiler }: { taxFiler: TaxFiler }) {
  const { t } = useTranslation();
  const router = useRouter();
  const save = useApiMutation(
    (body: TaxFiler) => api.put("/api/accounting/settings/tax-filer", body),
    {
      onSuccess: () => router.invalidate(),
      errorMessage: () => t("accSettings.taxFilerFailed"),
    },
  );

  async function submit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const fd = new FormData(e.currentTarget);
    const body = Object.fromEntries(
      TAX_FILER_FIELDS.map((key) => [key, ((fd.get(key) as string) ?? "").trim()]),
    ) as TaxFiler;
    await save.mutate(body);
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t("accSettings.taxFiler")}</CardTitle>
        <CardDescription>{t("accSettings.taxFilerHint")}</CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={submit} className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-3">
            <Field
              name="preparerFirstName"
              label={t("accSettings.preparerFirstName")}
              defaultValue={taxFiler.preparerFirstName}
            />
            <Field
              name="preparerLastName"
              label={t("accSettings.preparerLastName")}
              defaultValue={taxFiler.preparerLastName}
            />
            <Field
              name="preparerPhone"
              label={t("accSettings.preparerPhone")}
              defaultValue={taxFiler.preparerPhone}
            />
          </div>
          <div className="grid gap-4 sm:grid-cols-3">
            <Field
              name="signatoryFirstName"
              label={t("accSettings.signatoryFirstName")}
              defaultValue={taxFiler.signatoryFirstName}
            />
            <Field
              name="signatoryLastName"
              label={t("accSettings.signatoryLastName")}
              defaultValue={taxFiler.signatoryLastName}
            />
            <Field
              name="signatoryPosition"
              label={t("accSettings.signatoryPosition")}
              defaultValue={taxFiler.signatoryPosition}
            />
          </div>
          <Field name="nace" label={t("accSettings.nace")} defaultValue={taxFiler.nace} />
          {save.error && <p className="text-destructive text-sm">{save.error}</p>}
          <Button type="submit" disabled={save.pending}>
            {save.pending ? t("accSettings.saving") : t("accSettings.save")}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}
